import React from "react";
import ComparisonPageTemplate from "../../components/ComparisonPageTemplate/ComparisonPageTemplate";

const CANONICAL_URL = "https://teamgrid.ai/compare/teamgrid-ai-vs-employee-monitoring-software";

const FAQ_ITEMS = [
  {
    question: "How is TeamGrid different from employee monitoring software?",
    answer:
      "TeamGrid is designed to understand how work happens, not to watch employees. It uses AI to surface patterns, risks, and team-wide improvement areas without behavior-level tracking.",
  },
  {
    question: "What types of analytics does TeamGrid provide?",
    answer:
      "TeamGrid provides team productivity analytics such as delivery speed, collaboration friction, and overload detection. You see what's happening, why it's happening, and what to do next.",
  },
  {
    question: "Can TeamGrid replace my current productivity tracking tool?",
    answer:
      "Yes. TeamGrid replaces traditional productivity tracking tools with automated insight and employee productivity metrics that provide richer context and help drive decisions.",
  },
  {
    question: "How does TeamGrid support team performance tracking?",
    answer:
      "TeamGrid provides real-time and historical team performance tracking by surfacing blockers, inconsistencies, and early burnout signals without requiring individual behavior monitoring.",
  },
  {
    question: "Is this just another workforce analytics software?",
    answer:
      "TeamGrid is a fully AI-powered workforce intelligence platform that helps teams move from data collection to actionable decision-making with trust, speed, and clarity.",
  },
];

const JSON_LD_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL_URL}#faq`,
  mainEntity: [
    {
      "@type": "Question",
      name: "How is TeamGrid different from employee monitoring software?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid is designed to understand how work happens, not to watch employees. It uses AI to surface patterns, risks, and team-wide improvement areas without behavior-level tracking.",
      },
    },
    {
      "@type": "Question",
      name: "What types of analytics does TeamGrid provide?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid provides team productivity analytics such as delivery speed, collaboration friction, and overload detection. You see what's happening, why it's happening, and what to do next.",
      },
    },
    {
      "@type": "Question",
      name: "Can TeamGrid replace my current productivity tracking tool?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid replaces traditional productivity tracking tools with automated insight and employee productivity metrics that provide richer context and help drive decisions.",
      },
    },
    {
      "@type": "Question",
      name: "How does TeamGrid support team performance tracking?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid provides real-time and historical team performance tracking by surfacing blockers, inconsistencies, and early burnout signals without requiring individual behavior monitoring.",
      },
    },
    {
      "@type": "Question",
      name: "Is this just another workforce analytics software?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid is a fully AI-powered workforce intelligence platform that helps teams move from data collection to actionable decision-making with trust, speed, and clarity.",
      },
    },
  ],
};

const JSON_LD_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://teamgrid.ai/#website",
      url: "https://teamgrid.ai/",
      name: "TeamGrid",
    },
    {
      "@type": "Organization",
      "@id": "https://teamgrid.ai/#organization",
      name: "TeamGrid",
      url: "https://teamgrid.ai/",
    },
    {
      "@type": "WebPage",
      "@id": `${CANONICAL_URL}#webpage`,
      url: CANONICAL_URL,
      name: "Replace Monitoring Tools with TeamGrid: A True Workforce Intelligence Platform",
      description: "Employee monitoring shows you what your team is doing. TeamGrid shows you why work flows or stalls using AI-powered insight, not surveillance—making it a modern workforce intelligence platform.",
      isPartOf: { "@id": "https://teamgrid.ai/#website" },
      about: [
        { "@id": "https://teamgrid.ai/#teamgrid-app" },
        { "@id": "https://teamgrid.ai/#employee-monitoring-category" },
      ],
      breadcrumb: { "@id": `${CANONICAL_URL}#breadcrumbs` },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL_URL}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://teamgrid.ai/" },
        { "@type": "ListItem", position: 2, name: "Compare", item: "https://teamgrid.ai/compare/" },
        { "@type": "ListItem", position: 3, name: "TeamGrid vs Employee Monitoring Software", item: CANONICAL_URL },
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://teamgrid.ai/#teamgrid-app",
      name: "TeamGrid",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS",
      description: "TeamGrid is an AI-powered workforce intelligence platform that helps leaders understand how and why work happens using team-level analytics, automated summaries, and organizational insights—without surveillance.",
      url: "https://teamgrid.ai/",
      publisher: { "@id": "https://teamgrid.ai/#organization" },
      featureList: [
        "AI-driven workforce intelligence platform for real-time team insight",
        "Detects patterns, risks, and opportunities automatically",
        "Team productivity analytics focused on team-level trends",
        "Team performance tracking that highlights blockers and delivery risks",
        "Ask TeamGrid (AI workforce assistant) for instant insight and summaries",
        "Real-time, actionable dashboards with insight-led actions",
        "Daily, hourly, and weekly performance summaries",
        "One-click, presentation-ready exports and reports",
        "Built for remote, hybrid, and in-office teams",
        "Designed for clarity, trust, and cross-team adoption",
      ],
    },
    {
      "@type": "Thing",
      "@id": "https://teamgrid.ai/#employee-monitoring-category",
      name: "Employee Monitoring Software",
      description: "Tools that track employee activity such as time logs, system usage, and behavior-level monitoring to measure productivity.",
    },
    {
      "@type": "ItemList",
      "@id": `${CANONICAL_URL}#comparison`,
      name: "TeamGrid vs Employee Monitoring Software - Comparison",
      description: "Comparison of TeamGrid with employee monitoring software across philosophy, AI insights, workforce intelligence, analytics style, privacy fit, dashboards, and reporting.",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: 2,
      itemListElement: [
        { "@type": "ListItem", position: 1, item: { "@id": "https://teamgrid.ai/#teamgrid-app" } },
        { "@type": "ListItem", position: 2, item: { "@id": "https://teamgrid.ai/#employee-monitoring-category" } },
      ],
    },
  ],
};

const DATA_DECISIONS_TEAMGRID_BULLETS = [
  "Visibility comes from AI-detected patterns, not screen-level behavior",
  "Helps managers understand how work is flowing, not just what apps are used",
  "Teams embrace TeamGrid because it improves work not polices it",
  "That's why insight-driven organizations are replacing outdated tracking tools with TeamGrid: the workforce intelligence platform designed for performance not policing.",
];

const DATA_DECISIONS_COMPETITOR_BULLETS = [
  "Most tools track activity not impact. They show when someone's online, but not whether a process is working, a project is blocked, or a team is overloaded",
];

const DATA_DECISIONS_2_TEAMGRID_BULLETS = [
  "Team productivity analytics highlight inefficiencies and friction",
  "Ask TeamGrid for real-time insight like: \"Where are we losing time?\" or \"Who's overloaded right now?\"",
  "Clear, automated summaries enable fast, informed decisions",
];

const DATA_DECISIONS_2_COMPETITOR_BULLETS = [
  "Tracks time, activity, and sometimes screen use",
  "Focuses on employee productivity metrics without interpretation",
  "Creates overhead for managers who must make sense of the data",
];

const KEY_FEATURE_CARDS = [
  {
    title: "Ask TeamGrid – Your AI Workforce Assistant",
    intro: "Get instant insight into where time is lost, who's overloaded, and what to do next—with clear, automated summaries and no manual dashboard digging.",
    bullets: [],
    closing: "",
  },
  {
    title: "Not Just Productivity Tracking: True Workforce Analytics",
    intro: "TeamGrid surfaces blockers, delivery risks, and team-wide patterns so you can act on performance with clarity—not just track activity.",
    bullets: [],
    closing: "",
  },
];

const WorkforcePlatformComparison = () => (
  <ComparisonPageTemplate
    seoTitle="Workforce Intelligence Platform for Modern Teams | TeamGrid"
    seoDescription="Replace employee monitoring with a modern workforce intelligence platform. TeamGrid delivers AI-powered team insight, productivity analytics, and performance clarity without surveillance."
    seoCanonical={CANONICAL_URL}
    jsonLdGraph={JSON_LD_GRAPH}
    jsonLdFaq={JSON_LD_FAQ}
    heroBadge="Comparison Guide"
    heroTitleLine1="Replace Monitoring Tools with TeamGrid:"
    heroTitleLine2="A True Workforce Intelligence Platform"
    heroTitleLine2Teal
    heroTitleLine3={null}
    heroTitleLine4={null}
    heroDescriptionLines={[
      "Employee monitoring shows you what your team is doing.",
      "TeamGrid shows you why work flows or stalls using AI-powered insight, not surveillance.",
      "That is why modern teams are choosing TeamGrid as their workforce intelligence platform.",
    ]}
    competitorName="Employee Monitoring Software"
    trustedBadges={[
      "AI-driven workforce intelligence platform",
      "Ask TeamGrid: Get answers, not just data",
      "Understand flow, focus, and friction",
    ]}
    faqs={FAQ_ITEMS}
    faqTitle="Frequently Asked Questions"
    faqSubtitle="Common questions about TeamGrid vs. employee monitoring software."
    dataDecisionsHeading="Use Case 1: From Monitoring to Meaningful Insight"
    dataDecisionsSubtitle="Why Teams Are Moving Beyond Traditional Tracking Tools"
    dataDecisionsTeamGridBullets={DATA_DECISIONS_TEAMGRID_BULLETS}
    dataDecisionsCompetitorBullets={DATA_DECISIONS_COMPETITOR_BULLETS}
    dataDecisionsCompetitorCardTitle="The Problem with Legacy Monitoring Software"
    dataDecisions2Heading="Use Case 2: Moving from Data to Decisions"
    dataDecisions2Subtitle="Why Insight Beats a Basic Productivity Tracking Tool"
    dataDecisions2TeamGridBullets={DATA_DECISIONS_2_TEAMGRID_BULLETS}
    dataDecisions2CompetitorBullets={DATA_DECISIONS_2_COMPETITOR_BULLETS}
    dataDecisions2CompetitorCardTitle="With Employee Monitoring Software"
    keyFeaturesTitle="Why Teams Choose TeamGrid Over Monitoring Tools"
    keyFeaturesSubtitle="Ask TeamGrid – Your AI Workforce Assistant"
    keyFeatureCards={KEY_FEATURE_CARDS}
    outcomesTitle="Outcomes You'll See"
  />
);

export default WorkforcePlatformComparison;
